import { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowUpRight, CheckCircle, SpinnerGap, WarningCircle } from '@phosphor-icons/react';
import { listProjects } from '../lib/api';
import { markProjectOpened, wasProjectOpenedAfter } from '../lib/projectOpenHistory';

const REFRESH_INTERVAL_MS = 3000;
const MAX_READY_PROJECTS = 6;
const PROJECT_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const ACTIVE_PROJECT_STATUSES = new Set([
  'scoping',
  'planning',
  'plan_ready',
  'generating',
  'processing',
]);
const ACTIVE_RUN_STATUSES = new Set(['queued', 'running']);
const GENERIC_PROJECT_NAMES = new Set([
  '',
  'untitled project',
  'multi-agent composer',
]);

function projectKey(project) {
  return project.projectId || project.clientId || project.id;
}

function completionTimestamp(project) {
  return project.latestAgentRunFinishedAt || project.completedAt || project.updatedAt;
}

function activityTimestamp(project) {
  if (project.launchStatus) return project.createdAt || 0;
  if (isActiveProject(project)) {
    return project.latestAgentRunStartedAt || project.updatedAt || project.createdAt;
  }
  return completionTimestamp(project) || project.createdAt;
}

function isActiveProject(project) {
  const runStatus = String(project.latestAgentRunStatus || project.launchStatus || '').toLowerCase();
  const projectStatus = String(project.status || '').toLowerCase();
  return ACTIVE_RUN_STATUSES.has(runStatus)
    || ACTIVE_PROJECT_STATUSES.has(projectStatus)
    || ['planning', 'starting'].includes(String(project.launchStatus || '').toLowerCase());
}

function isReadyProject(project, userId) {
  const runStatus = String(project.latestAgentRunStatus || '').toLowerCase();
  const projectStatus = String(project.status || '').toLowerCase();
  if (runStatus === 'error' || project.launchStatus === 'failed') return false;
  if (projectStatus !== 'completed' && runStatus !== 'completed') return false;
  return !wasProjectOpenedAfter(
    userId,
    project.projectId,
    completionTimestamp(project),
  );
}

function isRecentProject(project) {
  const createdAt = new Date(project.createdAt || 0).getTime();
  return Number.isFinite(createdAt) && createdAt >= Date.now() - PROJECT_MAX_AGE_MS;
}

function mergeProjects(projects, optimisticProjects) {
  const merged = new Map();
  projects.forEach((project) => merged.set(projectKey(project), project));
  optimisticProjects.forEach((optimistic) => {
    const key = projectKey(optimistic);
    const persisted = merged.get(key);
    merged.set(key, persisted ? { ...optimistic, ...persisted } : optimistic);
  });
  return Array.from(merged.values()).sort((left, right) => {
    const leftTime = new Date(activityTimestamp(left) || 0).getTime();
    const rightTime = new Date(activityTimestamp(right) || 0).getTime();
    return rightTime - leftTime;
  });
}

function visibleProjects(projects, optimisticProjects, userId) {
  const relevant = mergeProjects(projects, optimisticProjects)
    .filter((project) => (
      isRecentProject(project)
      && (
        isActiveProject(project)
        || isReadyProject(project, userId)
        || project.launchStatus === 'failed'
      )
    ));
  let readyCount = 0;
  return relevant.filter((project) => {
    if (isActiveProject(project)) return true;
    if (project.launchStatus === 'failed') return true;
    readyCount += 1;
    return readyCount <= MAX_READY_PROJECTS;
  });
}

function projectPresentation(project) {
  const launchStatus = String(project.launchStatus || '').toLowerCase();
  const runStatus = String(project.latestAgentRunStatus || '').toLowerCase();
  const projectStatus = String(project.status || '').toLowerCase();
  const projectName = String(project.name || '').trim();
  const runTitle = String(project.latestAgentRunTitle || '').trim();
  const rawName = GENERIC_PROJECT_NAMES.has(projectName.toLowerCase())
    ? runTitle
    : projectName;
  const hasGeneratedName = !GENERIC_PROJECT_NAMES.has(rawName.toLowerCase());

  if (launchStatus === 'failed' || runStatus === 'error' || projectStatus === 'failed') {
    return {
      kind: 'failed',
      label: 'Needs attention',
      title: hasGeneratedName ? rawName : 'Workspace setup stopped',
      Icon: WarningCircle,
    };
  }
  if (launchStatus === 'planning') {
    return {
      kind: 'active',
      label: 'Planning',
      title: 'Starting up the workspace...',
      Icon: SpinnerGap,
    };
  }
  if (isActiveProject(project)) {
    return {
      kind: 'active',
      label: launchStatus === 'starting' && !runStatus ? 'Starting' : 'Working',
      title: hasGeneratedName ? rawName : 'Starting up the workspace...',
      Icon: SpinnerGap,
    };
  }
  return {
    kind: 'ready',
    label: 'Ready',
    title: hasGeneratedName ? rawName : 'Your project is ready',
    Icon: CheckCircle,
  };
}

function ProjectRunCard({ project, onOpen }) {
  const presentation = projectPresentation(project);
  const { Icon } = presentation;
  const canOpen = Boolean(project.projectId && project.canOpen !== false);
  const titleKey = `${projectKey(project)}:${presentation.title}`;

  return (
    <button
      type="button"
      className={`lp-project-run lp-project-run--${presentation.kind}`}
      onClick={() => canOpen && onOpen(project)}
      disabled={!canOpen}
      aria-label={canOpen ? `Open ${presentation.title}` : presentation.title}
    >
      <span className="lp-project-run__status" aria-hidden="true">
        <Icon
          size={18}
          weight={presentation.kind === 'ready' ? 'fill' : 'regular'}
          className={presentation.kind === 'active' ? 'lp-project-run__spinner' : undefined}
        />
      </span>
      <span className="lp-project-run__copy">
        <span className="lp-project-run__title" key={titleKey}>{presentation.title}</span>
      </span>
      <span className="lp-project-run__eyebrow">{presentation.label}</span>
      {canOpen && <ArrowUpRight className="lp-project-run__open" size={16} aria-hidden="true" />}
    </button>
  );
}

function ProjectRunsSkeleton() {
  return (
    <div className="lp-project-runs__list" aria-label="Loading project activity" aria-busy="true">
      {[0, 1].map((index) => (
        <div className="lp-project-run lp-project-run--skeleton" key={index} aria-hidden="true">
          <span className="lp-project-run__skeleton-icon" />
          <span className="lp-project-run__skeleton-copy">
            <span />
          </span>
          <span className="lp-project-run__skeleton-status" />
        </div>
      ))}
    </div>
  );
}

export default function LandingProjectRuns({ userId, optimisticProjects = [], onOpenProject, onViewAll }) {
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await listProjects({ page: 1, limit: 100, activityOnly: true });
      setProjects(response.projects || []);
    } catch (error) {
      // Keep the most recent successful snapshot and any optimistic cards.
      console.warn('Could not refresh landing project activity', error);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    refresh();
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'hidden') refresh();
    }, REFRESH_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [refresh, userId]);

  const visible = useMemo(
    () => visibleProjects(projects, optimisticProjects, userId),
    [optimisticProjects, projects, userId],
  );

  const handleOpen = useCallback((project) => {
    markProjectOpened(userId, project.projectId);
    setProjects((current) => current.filter((item) => item.projectId !== project.projectId));
    onOpenProject(project);
  }, [onOpenProject, userId]);

  if (!isLoading && visible.length === 0) return null;

  return (
    <section className="lp-project-runs" aria-labelledby="lp-project-runs-title">
      <div className="lp-project-runs__header">
        <h2 id="lp-project-runs-title">Project activity</h2>
        <button type="button" onClick={onViewAll}>View all</button>
      </div>
      {isLoading && visible.length === 0 ? (
        <ProjectRunsSkeleton />
      ) : (
        <div className="lp-project-runs__list" aria-live="polite">
          {visible.map((project) => (
            <ProjectRunCard project={project} onOpen={handleOpen} key={projectKey(project)} />
          ))}
        </div>
      )}
    </section>
  );
}

export { visibleProjects };
